import { useState } from 'react';
import { ArrowRight, User, Mail, Shield, Phone, Lock, Save, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { userService } from '@/services/userService';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const [fullName, setFullName] = useState(user?.fullName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSaveProfile = async () => {
    if (!user?.id) {
      setError('لم يتم العثور على معرّف المستخدم');
      return;
    }

    setSaving(true);
    setMessage('');
    setError('');

    try {
      const response = await userService.updateProfile({
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        address: user.address,
      });

      if (response.success === false) {
        throw new Error(response.message || 'فشل تحديث البيانات');
      }

      updateUser({
        ...user,
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });

      setMessage('تم تحديث بيانات الحساب بنجاح');
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        'حدث خطأ أثناء تحديث البيانات'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user?.id) {
      setError('لم يتم العثور على معرّف المستخدم');
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('يرجى إدخال جميع حقول كلمة المرور');
      return;
    }

    if (newPassword.length < 6) {
      setError('كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('تأكيد كلمة المرور غير مطابق');
      return;
    }

    setChangingPassword(true);
    setMessage('');
    setError('');

    try {
      /*
       * نستخدم endpoint تغيير كلمة المرور الموجود حاليًا.
       * إذا كان الـ backend يستخدم endpoint مختلفًا سنعدله بعد اختبار الطلب.
       */
      const response = await userService.updateProfile({
        currentPassword,
        newPassword,
      });

      if (response.success === false) {
        throw new Error(response.message || 'فشل تغيير كلمة المرور');
      }

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage('تم تغيير كلمة المرور بنجاح');
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        'حدث خطأ أثناء تغيير كلمة المرور'
      );
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="p-6" dir="rtl">
      <div className="max-w-4xl mx-auto">

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              الملف الشخصي
            </h1>
            <p className="text-gray-500 mt-1">
              إدارة بيانات حسابك الشخصي
            </p>
          </div>

          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
          >
            <ArrowRight className="w-5 h-5" />
            العودة إلى لوحة التحكم
          </button>
        </div>

        {message && (
          <div className="mb-5 p-4 rounded-lg bg-green-50 border border-green-200 text-green-700">
            {message}
          </div>
        )}

        {error && (
          <div className="mb-5 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">
            {error}
          </div>
        )}

        {/* البيانات الشخصية */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">

          <div className="bg-gradient-to-l from-orange-500 to-orange-600 p-8 text-white">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                <User className="w-10 h-10" />
              </div>

              <div>
                <h2 className="text-2xl font-bold">
                  {fullName || user?.username || 'المستخدم'}
                </h2>

                <p className="text-white/80 mt-1">
                  {user?.roleName || user?.role || 'مستخدم'}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                اسم المستخدم
              </label>

              <div className="relative">
                <User className="absolute right-3 top-3 w-5 h-5 text-gray-400" />

                <input
                  value={user?.username || ''}
                  disabled
                  className="w-full pr-10 pl-4 py-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                />
              </div>

              <p className="text-xs text-gray-400 mt-1">
                لا يمكن تغيير اسم المستخدم.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                الاسم الكامل
              </label>

              <div className="relative">
                <User className="absolute right-3 top-3 w-5 h-5 text-gray-400" />

                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  placeholder="أدخل الاسم الكامل"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                البريد الإلكتروني
              </label>

              <div className="relative">
                <Mail className="absolute right-3 top-3 w-5 h-5 text-gray-400" />

                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  placeholder="البريد الإلكتروني"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                رقم الهاتف
              </label>

              <div className="relative">
                <Phone className="absolute right-3 top-3 w-5 h-5 text-gray-400" />

                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  placeholder="رقم الهاتف"
                />
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <Shield className="w-5 h-5 text-orange-500" />

              <div>
                <p className="text-xs text-gray-500">الدور</p>
                <p className="font-semibold text-gray-800">
                  {user?.roleName || user?.role || '-'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSaveProfile}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white rounded-lg font-semibold transition"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري الحفظ...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  حفظ البيانات
                </>
              )}
            </button>

          </div>
        </div>

        {/* تغيير كلمة المرور */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-orange-600" />
              </div>

              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  تغيير كلمة المرور
                </h2>

                <p className="text-sm text-gray-500">
                  قم بتحديث كلمة المرور الخاصة بحسابك
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                كلمة المرور الحالية
              </label>

              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                placeholder="كلمة المرور الحالية"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                كلمة المرور الجديدة
              </label>

              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                placeholder="6 أحرف على الأقل"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                تأكيد كلمة المرور الجديدة
              </label>

              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                placeholder="أعد كتابة كلمة المرور الجديدة"
              />
            </div>

            <button
              type="button"
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gray-800 hover:bg-gray-900 disabled:bg-gray-400 text-white rounded-lg font-semibold transition"
            >
              {changingPassword ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري تغيير كلمة المرور...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  تغيير كلمة المرور
                </>
              )}
            </button>

          </div>
        </div>

      </div>
    </div>
  );
}
